import type { ApexOptions } from 'apexcharts';
import { format, parse } from 'date-fns';
import { cloneDeep, merge } from 'lodash-es';
import { useContext, useMemo } from 'react';
import { defaultGraphLabelStyle } from 'src/components/helper/ColorFormatter';
import { formatValue } from 'src/components/helper/NumberFormatters';
import { BrandContext } from 'src/contexts/BrandContext';
import type { IMetricsOptions } from 'src/core/types/metricsTypes';
import { useBrandPerformanceMetrics } from 'src/queries';

import { useGlobalStore } from '../../../../../store/store';
import { chartBaseAxis, chartColors, performanceChartMetricsProperty } from '../helpers/conts';
import { useBrandPerformanceChartStore } from '../store/store';

export const useGetChartData = (): { series: ApexOptions['series']; options: ApexOptions } => {
  const { data } = useBrandPerformanceMetrics();
  const {
    primaryMetric: { value: primaryMetricValue, label: primaryMetricLabel },
    secondaryMetric: { value: secondaryMetricValue, label: secondaryMetricLabel },
  } = useBrandPerformanceChartStore();

  const { brand } = useContext(BrandContext);
  const { currency, dates } = useGlobalStore();

  const [chartData, chartLabels, primaryMetricLabelPY, secondaryMetricLabelPY] = useMemo(() => {
    const cData = data?.data?.lineChart?.data || {};
    let cLabels = data?.data?.lineChart?.labels || [];
    if (dates.timeframe === 'mom') {
      cLabels = cLabels.map((label) =>
        format(parse(label, 'yyyy MMM, dd', new Date()), 'MMM yyyy'),
      );
    }
    const pLabelPY =
      cData?.[`py_${primaryMetricValue}`]?.label ?? `Previous Year ${primaryMetricLabel}`;
    const sMetricLabelPY =
      cData?.[`py_${secondaryMetricValue}`]?.label ?? `Previous Year ${secondaryMetricLabel}`;
    return [cData, cLabels, pLabelPY, sMetricLabelPY];
  }, [
    data,
    dates.timeframe,
    primaryMetricLabel,
    primaryMetricValue,
    secondaryMetricLabel,
    secondaryMetricValue,
  ]);

  const [series, metricOptions] = useMemo(
    (): [ApexAxisChartSeries, types: Record<string, IMetricsOptions>] => [
      [
        {
          name: primaryMetricLabel,
          data: chartData?.[primaryMetricValue]?.data || [],
          type: 'area',
          color: chartColors.primaryMetric,
          hidden: false,
        },
        {
          name: secondaryMetricLabel,
          data: chartData?.[secondaryMetricValue]?.data || [],
          type: 'area',
          color: chartColors.secondaryMetric,
          hidden: false,
        },
        {
          name: primaryMetricLabelPY,
          data: chartData?.[`py_${primaryMetricValue}`]?.data || [],
          type: 'line',
          color: chartColors.primaryMetric,
          hidden: primaryMetricLabelPY !== 'Previous Year Total Sales',
        },
        {
          name: secondaryMetricLabelPY,
          data: chartData?.[`py_${secondaryMetricValue}`]?.data || [],
          type: 'line',
          color: chartColors.secondaryMetric,
          hidden: secondaryMetricLabelPY !== 'Previous Year Total Sales',
        },
      ],
      {
        [primaryMetricLabel]: performanceChartMetricsProperty[primaryMetricLabel],
        [secondaryMetricLabel]: performanceChartMetricsProperty[secondaryMetricLabel],
        [primaryMetricLabelPY]: performanceChartMetricsProperty[primaryMetricLabel],
        [secondaryMetricLabelPY]: performanceChartMetricsProperty[secondaryMetricLabel],
      },
    ],
    [
      chartData,
      primaryMetricLabel,
      primaryMetricLabelPY,
      primaryMetricValue,
      secondaryMetricLabel,
      secondaryMetricLabelPY,
      secondaryMetricValue,
    ],
  );

  const activeAxis = useMemo(
    (): ApexOptions['yaxis'] => [
      merge(cloneDeep(chartBaseAxis), {
        seriesName: [primaryMetricLabel, primaryMetricLabelPY],
        // title: { text: primaryMetricLabel },
        opposite: false,
        labels: {
          formatter: (value: number) => {
            return formatValue(
              value,
              performanceChartMetricsProperty[primaryMetricLabel]?.axis,
              brand,
              currency,
            );
          },
        },
      }),
      merge(cloneDeep(chartBaseAxis), {
        seriesName: [secondaryMetricLabel, secondaryMetricLabelPY],
        // title: { text: secondaryMetricLabel },
        opposite: true,
        labels: {
          formatter: (value: number) => {
            return formatValue(
              value,
              performanceChartMetricsProperty[secondaryMetricLabel]?.axis,
              brand,
              currency,
            );
          },
        },
      }),
    ],
    [
      brand,
      currency,
      primaryMetricLabel,
      primaryMetricLabelPY,
      secondaryMetricLabel,
      secondaryMetricLabelPY,
    ],
  );

  const options = useMemo(
    (): ApexOptions => ({
      chart: {
        id: 'performanceMetricsChartLine',
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        dropShadow: {
          enabled: false,
        },
        animations: {
          enabled: false,
        },
        height: '100%',
        type: 'area',
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        floating: false,
        offsetY: 0,
        offsetX: 0,
      },
      fill: {
        type: ['gradient', 'gradient', 'solid', 'solid'],
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.5,
          opacityFrom: 0.7,
          opacityTo: 0.2,
          stops: [0, 90, 100],
        },
      },
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        curve: 'smooth',
        lineCap: 'butt',
        width: Array(series.length).fill(3),
        dashArray: [0, 0, 5, 5],
      },
      grid: {
        show: true,
        borderColor: '#eaecec',
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: true } },
      },
      markers: {
        size: 0,
        //size: Array(series.length).fill(5),
        strokeWidth: Array(series.length).fill(0),
        strokeOpacity: 1,
        fillOpacity: 1,
        hover: { sizeOffset: 1 },
      },
      tooltip: {
        shared: true,
        followCursor: true,
        y: {
          formatter: function (val, w) {
            try {
              return formatValue(
                val,
                metricOptions[series[w.seriesIndex].name || '']?.formatter,
                brand,
                currency,
              );
            } catch (error) {
              // console.log(error);
            }

            return formatValue(val, 'numberFormatter', brand, currency);
          },
        },
      },
      xaxis: {
        categories: chartLabels ?? [],
        type: 'category',
        labels: {
          style: defaultGraphLabelStyle,
        },
        tickPlacement: 'on',
      },
      yaxis: activeAxis,
    }),
    [activeAxis, brand, chartLabels, currency, metricOptions, series],
  );
  return { series, options };
};
